import { useContext, useState } from 'react';
import styled from '@emotion/styled';
import { useFloating, useHover, useInteractions } from '@floating-ui/react';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context.js';
import { trunc } from '@trinity/chat-components';

export type Props = {
  onClick: () => void;
  selected: boolean;
  name: string;
  truncLimit?: number;
};

function ToolTip({ title }: { title: string }) {
  return (
    <ExpandedTooltip>
      <Header>
        <HeaderLabel>{title}</HeaderLabel>
      </Header>
    </ExpandedTooltip>
  );
}

export function QuantAttributeTabButton({ onClick, selected, name, truncLimit = 20 }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'right',
  });
  const hover = useHover(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);
  const theming = useContext(AppThemingContext);
  const enabled = theming?.theme.enabled;

  const colors = enabled
    ? {
      // Whitelabeling enabled
      selectedBackground: theming?.palette?.primary?.main || '#28aae1',
      selectedTextColor: '#fff',
      unselectedBackground: '#fff',
      unselectedTextColor: theming?.palette?.primary?.main || '#28aae1',
      unselectedBorderColor: theming?.palette?.primary?.main || '#28aae1',
    }
    : {
      // Whitelabeling not enabled
      selectedBackground: '#28aae1',
      selectedTextColor: '#fff',
      unselectedBackground: '#fff',
      unselectedTextColor: '#28aae1',
      unselectedBorderColor: '#28aae1',
    };

  const isTruncated = name.length > truncLimit;
  const displayName = isTruncated ? trunc(name, truncLimit) : name;

  return (
    <TabButton
      selected={selected}
      onClick={onClick}
      colors={colors}>
      <span ref={refs.setReference} {...getReferenceProps()}>
        {displayName}
        {isTruncated && isOpen && (
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}>
            <ToolTip title={name} />
          </div>
        )}
      </span>
    </TabButton>
  );
}

const TabButton = styled.button<{
  selected?: boolean;
  colors: {
    selectedBackground: string;
    selectedTextColor: string;
    unselectedBackground: string;
    unselectedTextColor: string;
    unselectedBorderColor: string;
  };
}>(({ selected, colors }) => ({
  background: selected ? colors.selectedBackground : colors.unselectedBackground,
  color: selected ? colors.selectedTextColor : colors.unselectedTextColor,
  border: selected ? 'none' : `1px solid ${colors.unselectedBorderColor}`,
  fontWeight: 500,
  borderRadius: 6,
  padding: '6px 4px',
  fontSize: '1rem',
  cursor: 'pointer',
  transition: 'background 0.15s, color 0.15s, border 0.15s',
  width: 210,
  height: 35,
}));

const ExpandedTooltip = styled.div(({ theme }) => ({
  backgroundColor: theme.palette.white?.main || '#ffffff',
  borderRadius: '5px',
  border: `1px solid ${theme.palette.gray?.light1 || '#d3d3d3'}`,
  boxShadow: '0 2px 6px 2px rgba(60, 64, 67, .15)',
  boxSizing: 'border-box',
  padding: '12px',
  maxWidth: 200,
  zIndex: 1,
}));

const Header = styled.div({
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const HeaderLabel = styled.h1(({ theme }) => ({
  fontSize: '12px',
  fontFamily: theme.fonts?.semiBold || 'inherit',
}));
